import React, { ComponentType } from 'react';
import dynamic from 'next/dynamic';
import { openModalInstance } from './host';
import type { ModalRegistry } from '../manifest';

type ModalComponent<P> = ComponentType<P & { close: () => void }>;
type ModalLoader<P> = () => Promise<{ default: ModalComponent<P> }>;

type IsOptionalPayload<T> = keyof T extends never
  ? true
  : Partial<T> extends T
  ? true
  : false;

export type OpenModalAction<
  T extends keyof ModalRegistry = keyof ModalRegistry
> = {
  [K in T]: IsOptionalPayload<ModalRegistry[K]> extends true
    ? { type: K; payload?: ModalRegistry[K] }
    : { type: K; payload: ModalRegistry[K] };
}[T];

const registry = new Map<string, ModalComponent<unknown>>();

export function registerModal<T extends keyof ModalRegistry>(
  type: T,
  loader: ModalLoader<ModalRegistry[T]>
) {
  registry.set(
    type,
    dynamic(loader, { ssr: false }) as ModalComponent<unknown>
  );
}

export function openModal<T extends keyof ModalRegistry>(
  action: OpenModalAction<T>
) {
  const Component = registry.get(action.type) as
    | ModalComponent<ModalRegistry[T]>
    | undefined;
  if (!Component) {
    // eslint-disable-next-line no-console
    console.warn(
      `openModal: no modal registered for type "${String(action.type)}"`
    );
    return;
  }
  const payload = (
    'payload' in action ? action.payload : {}
  ) as ModalRegistry[T];
  const Modal = Component as ModalComponent<ModalRegistry[T]>;
  openModalInstance(({ close }) => (
    // @ts-expect-error payload is guaranteed to have required props by OpenModalAction type
    <Modal {...payload} close={close} />
  ));
}
